import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({ mode: 'light', toggleMode: () => {} });

export const useAppTheme = () => useContext(ThemeContext);

const THEME_KEY = 'xplora-theme-mode';

const CustomThemeProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => (localStorage.getItem(THEME_KEY) as ThemeMode) || 'light');

  useEffect(() => {
    localStorage.setItem(THEME_KEY, mode);
    // update CSS variables for legacy styles
    const root = document.documentElement;
    if (mode === 'dark') {
      root.style.setProperty('--bg', '#0f1720');
      root.style.setProperty('--surface', '#111827');
      root.style.setProperty('--text', '#e6eef6');
      root.style.setProperty('--muted', '#9aa6b2');
      root.style.setProperty('--primary', '#009cd8');
    } else {
      root.style.setProperty('--bg', '#fafafa');
      root.style.setProperty('--surface', '#ffffff');
      root.style.setProperty('--text', '#2b2b2b');
      root.style.setProperty('--muted', '#6c757d');
      root.style.setProperty('--primary', '#009cd8');
    }
  }, [mode]);

  const toggleMode = () => setMode(prev => (prev === 'light' ? 'dark' : 'light'));

  const muiTheme = useMemo(() =>
    createTheme({
      palette: {
        mode: mode === 'dark' ? 'dark' : 'light',
        primary: { main: '#009cd8' },
        background: { default: mode === 'dark' ? '#0f1720' : '#fafafa', paper: mode === 'dark' ? '#111827' : '#fff' },
        text: { primary: mode === 'dark' ? '#e6eef6' : '#2b2b2b' },
      },
      typography: { fontFamily: "'Inter', -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" },
    }),
    [mode],
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default CustomThemeProvider;
